import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Zap, Clock } from "lucide-react";

const FIELDS = [
  ["name", "Your name", "text", true], ["company_name", "Company name", "text", true],
  ["email", "Work email", "email", true], ["phone", "Mobile number", "tel", true],
  ["password", "Password (min 6 characters)", "password", true], ["industry", "Industry", "text", false],
  ["country", "Country", "text", false], ["city", "City", "text", false], ["website", "Website", "url", false],
];

function Pending({ message }) {
  return (
    <div className="text-center space-y-3" data-testid="register-pending">
      <div className="w-12 h-12 mx-auto rounded-full bg-amber-100 text-amber-700 flex items-center justify-center"><Clock className="w-6 h-6" /></div>
      <h2 className="text-xl font-bold">Registration received</h2>
      <p className="text-sm text-slate-600">{message}</p>
      <p className="text-xs text-slate-500">You will receive an email once your company is approved. You can then sign in.</p>
      <Link to="/login" className="inline-block text-emerald-700 font-medium hover:underline text-sm" data-testid="pending-login-link">Back to sign in</Link>
    </div>
  );
}

export default function Register() {
  const [f, setF] = useState(Object.fromEntries(FIELDS.map(([k]) => [k, ""])));
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    const body = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v.trim() || null]));
    body.password = f.password;
    try { const { data } = await api.post("/auth/register", body); setDone(data.message); }
    catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? `Please check: ${d.map((x) => x.loc?.slice(-1)[0]).join(", ")}` : d || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <Card className="w-full max-w-lg p-8 border-slate-200 shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 bg-emerald-600 rounded-xl flex items-center justify-center text-white"><Zap className="w-5 h-5" /></div>
          <div className="font-bold text-lg">WaggleAI</div>
        </div>
        {done ? <Pending message={done} /> : (
          <>
            <h2 className="text-2xl font-bold tracking-tight">Register your company</h2>
            <p className="text-sm text-slate-500 mt-1 mb-6">Every new company is reviewed by our team before it can sign in.</p>
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
              {FIELDS.map(([k, label, type, req]) => (
                <div key={k} className={k === "password" ? "sm:col-span-2" : ""}>
                  <Label htmlFor={k}>{label}{req && " *"}</Label>
                  <Input id={k} type={type} required={req} minLength={k === "password" ? 6 : undefined}
                    value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`register-${k.replace("_", "-")}-input`} />
                </div>
              ))}
              <Button type="submit" disabled={loading} className="sm:col-span-2 bg-emerald-600 hover:bg-emerald-700" data-testid="register-submit-button">
                {loading ? "Submitting…" : "Submit for approval"}
              </Button>
            </form>
            <div className="mt-6 text-sm text-center text-slate-500">
              Already have an account? <Link to="/login" className="text-emerald-700 font-medium hover:underline" data-testid="login-link">Sign in</Link>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
